import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ClickHandler } from '../../../../utils/clickEvent';
import { PinInfo } from './stopTypes';

// Alias to avoid collision with our GoogleMap type import.
type PositionMap = globalThis.Map<number, { lat: string; lng: string }>; // eslint-disable-line no-undef
const createPositionMap = (): PositionMap => new Map<number, { lat: string; lng: string }>();

// Pins closer than this in pixels are considered overlapping.
const OVERLAP_THRESHOLD_PX = 35;

// Radius in pixels for the spider fan-out.
const SPIDER_RADIUS_PX = 60;

// Brief cooldown after expanding to prevent popover flash on pins that land under the cursor.
const EXPANDING_COOLDOWN_MS = 300;

type SpiderfyCluster = {
    key: string;
    centerLat: number;
    centerLng: number;
    pinIndices: number[];
};

/**
 * Finds clusters of overlapping pins based on their pixel proximity at the
 * current zoom level. Uses a simple greedy algorithm — fine for ≤20 pins.
 */
const findOverlappingClusters = (
    pinInfos: PinInfo[],
    map: google.maps.Map,
    maps: typeof google.maps,
): SpiderfyCluster[] => {
    const projection = map.getProjection();
    if (!projection) return [];

    const zoom = map.getZoom();
    if (zoom === undefined) return [];
    const scale = Math.pow(2, zoom);

    // Convert each pin to world-pixel coordinates. If a pin fails to project
    //  (shouldn't happen once the map is ready), it gets a sentinel that
    //  won't cluster with anything else.
    const pixelCoords = pinInfos.map((pin) => {
        const latLng = new maps.LatLng(parseFloat(pin.stop.lat), parseFloat(pin.stop.lng));
        const worldPoint = projection.fromLatLngToPoint(latLng);
        if (!worldPoint) return { x: Infinity, y: Infinity };
        return { x: worldPoint.x * scale, y: worldPoint.y * scale };
    });

    // Greedy clustering: assign each pin to the first cluster it overlaps with.
    const assigned = new Set<number>();
    const clusters: SpiderfyCluster[] = [];

    for (let i = 0; i < pixelCoords.length; i++) {
        if (assigned.has(i)) continue;

        const group = [i];
        assigned.add(i);

        for (let j = i + 1; j < pixelCoords.length; j++) {
            if (assigned.has(j)) continue;
            const dx = pixelCoords[i].x - pixelCoords[j].x;
            const dy = pixelCoords[i].y - pixelCoords[j].y;
            if (Math.sqrt(dx * dx + dy * dy) < OVERLAP_THRESHOLD_PX) {
                group.push(j);
                assigned.add(j);
            }
        }

        if (group.length >= 2) {
            // Cluster center is the average lat/lng of the grouped pins.
            let sumLat = 0;
            let sumLng = 0;
            for (const idx of group) {
                sumLat += parseFloat(pinInfos[idx].stop.lat);
                sumLng += parseFloat(pinInfos[idx].stop.lng);
            }
            clusters.push({
                key: group.sort((a, b) => a - b).join(','),
                centerLat: sumLat / group.length,
                centerLng: sumLng / group.length,
                pinIndices: group,
            });
        }
    }

    return clusters;
};

/**
 * Computes fanned-out lat/lng positions for each pin in a cluster,
 * arranged in a circle around the cluster center.
 */
const computeSpiderfiedPositions = (
    cluster: SpiderfyCluster,
    map: google.maps.Map,
    maps: typeof google.maps,
): PositionMap => {
    const positions = createPositionMap();
    const projection = map.getProjection();
    if (!projection) return positions;

    const zoom = map.getZoom();
    if (zoom === undefined) return positions;
    const scale = Math.pow(2, zoom);
    const centerLatLng = new maps.LatLng(cluster.centerLat, cluster.centerLng);
    const centerPoint = projection.fromLatLngToPoint(centerLatLng);
    if (!centerPoint) return positions;

    const n = cluster.pinIndices.length;
    const angleStep = (2 * Math.PI) / n;
    // Start from the top (-PI/2) for a nicer visual layout.
    const startAngle = -Math.PI / 2;

    for (let i = 0; i < n; i++) {
        const angle = startAngle + angleStep * i;
        const offsetX = (SPIDER_RADIUS_PX / scale) * Math.cos(angle);
        const offsetY = (SPIDER_RADIUS_PX / scale) * Math.sin(angle);
        const newPoint = new maps.Point(centerPoint.x + offsetX, centerPoint.y + offsetY);
        const newLatLng = projection.fromPointToLatLng(newPoint);
        if (!newLatLng) continue;

        positions.set(cluster.pinIndices[i], {
            lat: String(newLatLng.lat()),
            lng: String(newLatLng.lng()),
        });
    }

    return positions;
};

export const useSpiderfy = ({
    map,
    maps,
    allPinInfos,
    enabled,
}: {
    map: google.maps.Map | null;
    maps: typeof google.maps | null;
    allPinInfos: PinInfo[];
    enabled: boolean;
}) => {
    const [expandedClusterKey, setExpandedClusterKey] = useState<string | null>(null);
    const [isExpanding, setIsExpanding] = useState(false);
    const expandingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const polylineRefs = useRef<google.maps.Polyline[]>([]);

    const clearLegs = useCallback(() => {
        polylineRefs.current.forEach((leg) => leg.setMap(null));
        polylineRefs.current = [];
    }, []);

    const collapse = useCallback(() => {
        clearLegs();
        setExpandedClusterKey(null);
    }, [clearLegs]);

    // Detect clusters whenever pins or zoom changes.
    const [zoom, setZoom] = useState(0);

    useEffect(() => {
        if (!map || !enabled) return;
        setZoom(map.getZoom() ?? 0);
        const listener = map.addListener('zoom_changed', () => {
            setZoom(map.getZoom() ?? 0);
            collapse();
        });
        return () => listener.remove();
    }, [map, enabled, collapse]);

    // Collapse spider when clicking the map background.
    useEffect(() => {
        if (!map || !enabled) return;
        const listener = map.addListener('click', collapse);
        return () => listener.remove();
    }, [map, enabled, collapse]);

    const clusters = useMemo(() => {
        if (!enabled || !map || !maps) return [];
        // zoom is included so clusters recalculate on zoom change.
        void zoom;
        return findOverlappingClusters(allPinInfos, map, maps);
    }, [enabled, map, maps, allPinInfos, zoom]);

    // Build a lookup: pinIndex -> cluster it belongs to.
    const pinToCluster = useMemo(() => {
        const lookup = new Map<number, SpiderfyCluster>();
        for (const cluster of clusters) {
            for (const idx of cluster.pinIndices) {
                lookup.set(idx, cluster);
            }
        }
        return lookup;
    }, [clusters]);

    // Compute fanned-out positions for the expanded cluster.
    const spiderfiedPositions = useMemo(() => {
        if (!expandedClusterKey || !map || !maps) return createPositionMap();
        const cluster = clusters.find((c) => c.key === expandedClusterKey);
        if (!cluster) return createPositionMap();
        return computeSpiderfiedPositions(cluster, map, maps);
    }, [expandedClusterKey, clusters, map, maps]);

    // Draw spider legs whenever positions change.
    useEffect(() => {
        clearLegs();
        if (!map || !maps || spiderfiedPositions.size === 0) return;

        const cluster = clusters.find((c) => c.key === expandedClusterKey);
        if (!cluster) return;

        const legs: google.maps.Polyline[] = [];
        spiderfiedPositions.forEach((pos: { lat: string; lng: string }) => {
            const leg = new maps.Polyline({
                path: [
                    { lat: cluster.centerLat, lng: cluster.centerLng },
                    { lat: parseFloat(pos.lat), lng: parseFloat(pos.lng) },
                ],
                geodesic: false,
                strokeColor: '#999',
                strokeOpacity: 0.6,
                strokeWeight: 1.5,
            });
            leg.setMap(map);
            legs.push(leg);
        });
        polylineRefs.current = legs;
    }, [map, maps, spiderfiedPositions, clusters, expandedClusterKey, clearLegs]);

    // Clean up on unmount.
    useEffect(() => clearLegs, [clearLegs]);
    useEffect(() => {
        return () => {
            if (expandingTimerRef.current) clearTimeout(expandingTimerRef.current);
        };
    }, []);

    const getPinPosition = useCallback(
        (index: number, originalLat: string, originalLng: string) => {
            const overridden = spiderfiedPositions.get(index);
            if (overridden) return overridden;
            return { lat: originalLat, lng: originalLng };
        },
        [spiderfiedPositions],
    );

    const handlePinClick = useCallback(
        (index: number, originalOnClick?: ClickHandler): ClickHandler | undefined => {
            const cluster = pinToCluster.get(index);
            if (!cluster) return originalOnClick;

            // If this pin's cluster is already expanded, let the original click through
            // but stop propagation so the map click listener doesn't collapse the spider.
            if (expandedClusterKey === cluster.key) {
                return (event) => {
                    event.stopPropagation();
                    originalOnClick?.(event);
                };
            }

            // Otherwise, expand this cluster on click.
            return (event) => {
                event.stopPropagation();
                collapse();
                setExpandedClusterKey(cluster.key);
                // Brief cooldown to prevent hover state on pins that land under the cursor.
                setIsExpanding(true);
                if (expandingTimerRef.current) clearTimeout(expandingTimerRef.current);
                expandingTimerRef.current = setTimeout(
                    () => setIsExpanding(false),
                    EXPANDING_COOLDOWN_MS,
                );
            };
        },
        [pinToCluster, expandedClusterKey, collapse],
    );

    const shouldShowPopover = useCallback(
        (index: number): boolean => {
            if (isExpanding) return false;
            const cluster = pinToCluster.get(index);
            if (!cluster) return true;
            // Only show popover for pins in the currently expanded cluster.
            return expandedClusterKey === cluster.key;
        },
        [pinToCluster, expandedClusterKey, isExpanding],
    );

    return { getPinPosition, handlePinClick, shouldShowPopover };
};
